/**
 * Simple PDF export for a medication card.
 *
 * We build the PDF directly with jsPDF text/table rendering rather than
 * rasterising the DOM — text is sharper, print-ready, and stays legible
 * at large-print sizes. Everything fits on US letter portrait.
 */
import jsPDF from "jspdf";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIME_ORDER = ["morning", "noon", "evening", "bedtime"];
const TIME_LABEL = {
  morning: "Morning",
  noon: "Noon",
  evening: "Evening",
  bedtime: "Bedtime",
};

export async function exportMedCardPDF(profile) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  let y = margin;

  const line = (text, opts = {}) => {
    const { size = 11, bold = false, color = "#1C2B23", spaceAfter = 4 } = opts;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(color);
    const split = doc.splitTextToSize(text, pageW - margin * 2);
    doc.text(split, margin, y);
    y += split.length * size * 1.15 + spaceAfter;
  };

  const rule = () => {
    doc.setDrawColor("#E5E2DC");
    doc.line(margin, y, pageW - margin, y);
    y += 12;
  };

  const ensureRoom = (needed) => {
    if (y + needed > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // --- Header ---
  line("CENTENNIAL · MEDICATION SAFETY CARD", { size: 9, bold: true, color: "#2B5339", spaceAfter: 4 });
  line(profile.name, { size: 22, bold: true, spaceAfter: 2 });
  line(`Prepared ${new Date().toLocaleDateString()}`, { size: 9, color: "#5C6B62", spaceAfter: 8 });
  rule();

  // --- Safety check ---
  line("FIVE-POINT SAFETY CHECK", { size: 9, bold: true, color: "#2B5339" });
  [
    "1  Medication name",
    "2  Strength (mg / mcg / IU)",
    "3  Imprint on both sides of the pill",
    "4  Shape and dosage form",
    "5  Prescription label and manufacturer",
  ].forEach((l) => line(l, { size: 11, spaceAfter: 2 }));
  line(
    "Never identify a medication using color or shape alone. Medication appearance may vary by manufacturer. If uncertain, do not take it until a pharmacist confirms it.",
    { size: 9, color: "#C26747", spaceAfter: 8 },
  );
  rule();

  // --- Medication list ---
  line("MEDICATIONS", { size: 9, bold: true, color: "#2B5339" });
  profile.meds.forEach((m, i) => {
    ensureRoom(90);
    line(
      `${i + 1}. ${m.generic_name}${m.brand_name ? ` (${m.brand_name})` : ""}`,
      { size: 14, bold: true, spaceAfter: 2 },
    );
    line(`${m.strength}   ·   ${(m.times || []).map((t) => TIME_LABEL[t] || t).join(", ") || "—"}`, {
      size: 11,
      color: "#2B5339",
      spaceAfter: 2,
    });
    if (m.purpose) line(`For: ${m.purpose}`, { size: 10, color: "#5C6B62", spaceAfter: 2 });
    if (m.directions) line(m.directions, { size: 10, spaceAfter: 2 });
    const meta = [
      m.form && `Form: ${m.form}`,
      m.imprint_front && `Imprint (front): ${m.imprint_front}`,
      m.imprint_back && `Imprint (back): ${m.imprint_back}`,
      m.manufacturer && `Mfr: ${m.manufacturer}`,
      m.pharmacy && `Pharmacy: ${m.pharmacy}`,
    ].filter(Boolean).join("  ·  ");
    if (meta) line(meta, { size: 9, color: "#5C6B62", spaceAfter: 2 });
    if (m.do_not_confuse)
      line(`Do not confuse with: ${m.do_not_confuse}`, { size: 9, color: "#C26747", spaceAfter: 6 });
    else y += 4;
  });

  // --- Weekly check-off grid ---
  doc.addPage();
  y = margin;
  line("WEEKLY MEDICATION CARD", { size: 9, bold: true, color: "#2B5339" });
  line(profile.name, { size: 16, bold: true, spaceAfter: 8 });

  for (const timeKey of TIME_ORDER) {
    const meds = profile.meds.filter((m) => (m.times || []).includes(timeKey));
    if (!meds.length) continue;
    ensureRoom(60 + meds.length * 24);
    line(TIME_LABEL[timeKey].toUpperCase(), { size: 10, bold: true, color: "#2B5339", spaceAfter: 4 });

    // Table header
    const colW = (pageW - margin * 2 - 180) / DAYS.length;
    doc.setFontSize(9);
    doc.setDrawColor("#E5E2DC");
    doc.setFillColor("#F9F8F6");
    doc.rect(margin, y, 180, 22, "FD");
    doc.setTextColor("#5C6B62");
    doc.text("Medication", margin + 6, y + 14);
    DAYS.forEach((d, i) => {
      const x = margin + 180 + i * colW;
      doc.rect(x, y, colW, 22, "FD");
      doc.text(d, x + colW / 2, y + 14, { align: "center" });
    });
    y += 22;

    // Table rows
    meds.forEach((m) => {
      doc.setFillColor("#FFFFFF");
      doc.rect(margin, y, 180, 28, "FD");
      doc.setTextColor("#1C2B23");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(m.generic_name, margin + 6, y + 12);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor("#2B5339");
      doc.text(m.strength, margin + 6, y + 24);
      DAYS.forEach((_, i) => {
        const x = margin + 180 + i * colW;
        doc.setFillColor("#FFFFFF");
        doc.setDrawColor("#E5E2DC");
        doc.rect(x, y, colW, 28, "FD");
        doc.setDrawColor("#5C6B62");
        doc.setLineWidth(1.2);
        doc.rect(x + colW / 2 - 7, y + 8, 14, 14);
        doc.setLineWidth(0.4);
      });
      y += 28;
    });

    y += 12;
  }

  // --- Footer ---
  ensureRoom(80);
  rule();
  line(
    "This card is for medication organization and education only. It does not replace a pharmacist, physician, prescription label, or professional medical advice. Do not start, stop, split, combine, or change a medication based on this card. Poison Control (US): 1-800-222-1222.",
    { size: 8, color: "#5C6B62", spaceAfter: 0 },
  );

  const filename = `centennial-medication-card-${profile.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-+|-+$/g, "")}.pdf`;
  doc.save(filename);
}
